import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ChatPage.css';
import momiboLogo from '../assets/momibo03-momibo.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function ChatPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isFloating, setIsFloating] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/history`);
      setHistory(response.data.history);
    } catch (error) {
      console.error('履歴の読み込みエラー:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview({ type: 'image', data: reader.result, name: file.name });
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview({ type: 'file', name: file.name, size: file.size });
      }
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const streamText = (text, callback) => {
    let index = 0;
    setStreamingText('');
    setIsStreaming(true);

    const interval = setInterval(() => {
      if (index < text.length) {
        setStreamingText(prev => prev + text[index]);
        index++;
      } else {
        clearInterval(interval);
        setIsStreaming(false);
        callback();
      }
    }, 20);
  };

  const handleKeyDown = (e) => {
    // IME変換中（日本語入力中）はEnterキーを無視
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  const openInPopup = () => {
    const width = 420;
    const height = 650;
    const left = window.screen.width - width - 20;
    const top = window.screen.height - height - 80;

    window.open(
      '/app/chat',
      'MomiboChat',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    );
  };

  // ポップアップウィンドウかどうかを判定
  const isPopup = window.opener !== null || window.name === 'MomiboChat';

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!question.trim() && !selectedFile) return;

    const userMessage = {
      role: 'user',
      content: question,
      file: filePreview
    };
    setMessages([...messages, userMessage]);
    setLoading(true);
    const currentQuestion = question;
    setQuestion('');
    const currentFile = selectedFile;
    removeFile();

    try {
      const formData = new FormData();
      formData.append('question', currentQuestion);
      if (currentFile) {
        formData.append('file', currentFile);
      }

      const response = await axios.post(`${API_URL}/chat`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setLoading(false);

      streamText(response.data.answer, () => {
        const aiMessage = {
          role: 'assistant',
          content: response.data.answer,
          sources: response.data.sources,
          chart: response.data.chart,
          table: response.data.table,
          extracted_images: response.data.extracted_images,
          images: response.data.images
        };
        setMessages(prev => [...prev, aiMessage]);
        setStreamingText('');
        loadHistory();
      });

    } catch (error) {
      console.error('メッセージ送信エラー:', error);
      setLoading(false);
      const errorContent = error.response?.data?.error || 'エラーが発生しました。もう一度お試しください。';
      const errorMessage = {
        role: 'assistant',
        content: errorContent
      };
      setMessages([...messages, userMessage, errorMessage]);
    }
  };

  return (
    <article className={`chat-page ${isFloating ? 'floating-mode' : ''} ${isMinimized ? 'minimized' : ''} ${isPopup ? 'popup-mode' : ''}`}>
      <header className="chat-header">
        {!isPopup && (
          <button className="home-btn" onClick={() => navigate('/')}>
            🏠
          </button>
        )}
        <div className="header-logo">Momibo</div>
        {!isPopup ? (
          <button
            className="floating-toggle-btn"
            onClick={openInPopup}
            title="別ウィンドウで開く"
          >
            ⧉
          </button>
        ) : (
          <button
            className="floating-toggle-btn"
            onClick={() => window.close()}
            title="閉じる"
          >
            ✕
          </button>
        )}
      </header>

      {isFloating && !isMinimized && (
        <button
          className="minimize-btn"
          onClick={() => setIsMinimized(true)}
          title="最小化"
        >
          ─
        </button>
      )}

      {isFloating && isMinimized && (
        <button
          className="chat-bubble"
          onClick={() => setIsMinimized(false)}
        >
          <img src={momiboLogo} alt="Momibo" />
        </button>
      )}

      <div className="container-xxl">
        <div className="title-box">
          <div className="commissioner-title-text">Chat Messages</div>
        </div>

        <div className="message-box">
          <div className="chat-message">
            <div className="data-box">
              <div className="chat-data-box">
                {messages.map((msg, index) => (
                  <div key={index}>
                    {msg.role === 'user' ? (
                      <div className="qry-area">
                        <div className="direct-qry-info">
                          <div className="direct-qry-name">あなた</div>
                        </div>
                        <div className="direct-qry-text-box">
                          <img className="direct-qry-img" src="https://img.icons8.com/color/36/000000/administrator-male.png" alt="user" />
                          <div className="direct-qry-content">
                            {msg.file && msg.file.type === 'image' && (
                              <img src={msg.file.data} alt="uploaded" className="message-image" />
                            )}
                            {msg.file && msg.file.type === 'file' && (
                              <div className="message-file">
                                📄 {msg.file.name} ({(msg.file.size / 1024).toFixed(1)} KB)
                              </div>
                            )}
                            {msg.content && <div className="direct-qry-text">{msg.content}</div>}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="res-area">
                        <div className="direct-res-info">
                          <div className="direct-res-name">Momibo</div>
                        </div>
                        <div className="direct-res-text-box">
                          <img className="direct-res-img" src="https://img.icons8.com/office/36/000000/person-female.png" alt="momibo" />
                          <div className="direct-res-content">
                            <div className="direct-res-text">{msg.content}</div>
                            {msg.chart && (
                              <img src={msg.chart} alt="chart" className="response-chart" />
                            )}
                            {msg.table && (
                              <div className="response-table" dangerouslySetInnerHTML={{ __html: msg.table }} />
                            )}
                            {msg.images && msg.images.length > 0 && (
                              <div className="found-images-container">
                                <div className="found-images-header">関連画像 ({msg.images.length}個)</div>
                                <div className="found-images-grid">
                                  {msg.images.map((img, idx) => (
                                    <div key={idx} className="found-image-item">
                                      <img src={img.data} alt={img.filename} className="found-image" />
                                      <div className="found-image-name">{img.filename}</div>
                                      <a
                                        href={img.data}
                                        download={img.filename}
                                        className="download-image-btn"
                                      >
                                        ダウンロード
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {msg.extracted_images && msg.extracted_images.length > 0 && (
                              <div className="extracted-images-container">
                                <div className="extracted-images-header">抽出された画像 ({msg.extracted_images.length}個)</div>
                                <div className="extracted-images-grid">
                                  {msg.extracted_images.map((img, idx) => (
                                    <div key={idx} className="extracted-image-item">
                                      <img src={img.data} alt={`extracted-${idx}`} className="extracted-image" />
                                      <div className="extracted-image-info">
                                        ページ {img.page} - {img.format.toUpperCase()} ({(img.size / 1024).toFixed(1)} KB)
                                      </div>
                                      <a
                                        href={img.data}
                                        download={`image-page${img.page}-${idx + 1}.${img.format}`}
                                        className="download-image-btn"
                                      >
                                        ダウンロード
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="res-area">
                    <div className="direct-res-info">
                      <div className="direct-res-name">Momibo</div>
                    </div>
                    <div className="direct-res-text-box">
                      <img className="direct-res-img" src="https://img.icons8.com/office/36/000000/person-female.png" alt="momibo" />
                      <div className="direct-res-text loading">回答を生成中...</div>
                    </div>
                  </div>
                )}
                {isStreaming && (
                  <div className="res-area">
                    <div className="direct-res-info">
                      <div className="direct-res-name">Momibo</div>
                    </div>
                    <div className="direct-res-text-box">
                      <img className="direct-res-img" src="https://img.icons8.com/office/36/000000/person-female.png" alt="momibo" />
                      <div className="direct-res-text streaming">{streamingText}<span className="cursor">▋</span></div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className="question-area">
              {filePreview && (
                <div className="file-preview-container">
                  {filePreview.type === 'image' ? (
                    <img src={filePreview.data} alt="preview" className="file-preview-image" />
                  ) : (
                    <div className="file-preview-doc">
                      📄 {filePreview.name} ({(filePreview.size / 1024).toFixed(1)} KB)
                    </div>
                  )}
                  <button type="button" className="remove-file-btn" onClick={removeFile}>✕</button>
                </div>
              )}
              <div className="question-box">
                <form onSubmit={sendMessage}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="attach-button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                    title="ファイルを添付"
                  >
                    📎
                  </button>
                  <textarea
                    className="textarea-t"
                    rows="3"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="質問をどうぞ・・（Enterで送信、Shift+Enterで改行）"
                    disabled={loading}
                  />
                  <button className="submit-button" type="submit" disabled={loading || (!question.trim() && !selectedFile)}>
                    送信
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default ChatPage;
